// Monthly streaming report for July 2026.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202607TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-07-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-07-top-party-games-and-streamers',
    route: '/party-central/reports/2026-07-top-party-games-and-streamers',
    seo: {
        title: 'July 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for July 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'July 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'green',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-08-06T00:00:00.000Z'),
        updatedAt: new Date('2026-08-06T00:00:00.000Z'),
        reportMonth: '2026-07',
        dataRange: {
            start: '2026-07-01',
            end: '2026-07-31',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('July was the busiest month in this report series so far. We recorded 269,898 party-game streams from 118,110 streamers, up 51.7% from June. Nearly all of that growth came from '),
                    em('MECCHA CHAMELEON'),
                    text(', which appeared in our data for a full month for the first time. '),
                    em('MECCHA CHAMELEON'),
                    text(' reached more than 61,000 streamers in at least 35 languages, and accounted for more than one in three tracked streams during July.'),
                ]),
                p([
                    em('Ragnarok Origin: Classic'),
                    text(' ranked first by Party Score after its Americas launch on July 23. Most of its activity came from Portuguese-language channels. Party Score balances streamer reach, audience size, and stickiness. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' for more about how the data is collected.'),
                ]),
                p([
                    link('https://twitch.tv/ponce', 'Ponce'),
                    text(' ranked first in Streamer Party Score, with '),
                    link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                    text(' and '),
                    link('https://twitch.tv/dooleynoted', 'DooleyNoted'),
                    text(' also sharing a score of 99.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('MECCHA CHAMELEON'),
                            text(" was July's biggest game by stream volume, but it ranked 25th by Party Score with 88. Most streamers appeared only once or twice. Party Score also rewards repeat activity and audience size, which allows smaller games with a strong following to shine. That's why "),
                            em('Ragnarok Origin: Classic'),
                            text(' landed in first place this month after its Americas launch on July 23. Check out all games by Party Score in our new '),
                            link('/party-central?measure=gamePartyScore&x=game&grain=&from=2026-07-01T00%3A00%3A00.000Z&to=2026-08-01T00%3A00%3A00.000Z', 'data explorer'),
                            text('.'),
                        ]),
                        p([
                            em('Riichi City'),
                            text(' was the surprise new entrant, jumping from 64th in June to fifth in July behind frequent play from '),
                            link('https://twitch.tv/fer123snake', 'fer123snake'),
                            text(' and a huge bump in viewership from three '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' appearances.'),
                        ]),
                        p([
                            em('Gartic Phone'),
                            text(' held steady in the third spot after making a large leap from 28th in May to third in June. '),
                            em('Diablo II: Resurrected'),
                            text(', '),
                            em('Super Smash Bros. Ultimate'),
                            text(', and '),
                            em('Mario Kart World'),
                            text(' also rose three places from June as their average audiences increased. The largest drop at the top of the chart was '),
                            em('UNO'),
                            text(', from fifth to 13th as average viewers fell from 22 to 13.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    'Ragnarok Origin: Classic',
                                    '95',
                                    '1,357',
                                    '290',
                                    '4.7',
                                    '53',
                                ],
                                ['2', 'nanos world', '95', '2,183', '141', '15.5', '49'],
                                ['3', 'Gartic Phone', '93', '10,251', '2,923', '3.5', '23'],
                                [
                                    '4',
                                    'Diablo II: Resurrected',
                                    '93',
                                    '5,116',
                                    '895',
                                    '5.7',
                                    '17',
                                ],
                                ['5', 'Riichi City', '93', '620', '192', '3.2', '56'],
                                ['6', 'Pinball', '93', '1,250', '162', '7.7', '23'],
                                [
                                    '7',
                                    'Super Smash Bros. Ultimate',
                                    '92',
                                    '11,085',
                                    '3,310',
                                    '3.3',
                                    '18',
                                ],
                                [
                                    '8',
                                    'Mario Kart World',
                                    '92',
                                    '5,615',
                                    '2,250',
                                    '2.5',
                                    '31',
                                ],
                                ['9', 'Words On Stream', '92', '7,293', '600', '12.2', '15'],
                                ['10', 'Mahjong Soul', '91', '3,408', '1,276', '2.7', '25'],
                                [
                                    '11',
                                    'Just Dance 2026 Edition',
                                    '91',
                                    '464',
                                    '185',
                                    '2.5',
                                    '39',
                                ],
                                [
                                    '12',
                                    'Jackbox Party Packs',
                                    '90',
                                    '3,936',
                                    '1,907',
                                    '2.1',
                                    '30',
                                ],
                                ['13', 'UNO', '90', '1,899', '500', '3.8', '13'],
                                ['14', 'Whatsamusic', '90', '267', '94', '2.8', '34'],
                                ['15', 'Pokémon Stadium', '90', '214', '81', '2.6', '61'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(', '),
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(', and '),
                            link('https://twitch.tv/dooleynoted', 'DooleyNoted'),
                            text(' shared the top score of 99. Ponce edged out KYR and Dooley for first place by averaging 3,451 viewers across the 14 times we saw them live playing party games. '),
                            em('Lycans'),
                            text(" was the French streamer's top choice, played every Tuesday of the month for more than 4,000 viewers on average."),
                        ]),
                        p([
                            text("Eight of July's top 15 were absent from the June list. "),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' brought a large audience to six different games. We spotted '),
                            link('https://twitch.tv/paache_', 'paache_'),
                            text(' streaming 10 more times this month than last, including five observations of '),
                            em('Mario Kart World'),
                            text(' and three extra on '),
                            em('Wii Sports'),
                            text('. '),
                            link('https://twitch.tv/hcjustin', 'HCJustin'),
                            text(' cracked the list after adding streams of '),
                            em('PlateUp!'),
                            text(', '),
                            em('LEGO Party!'),
                            text(', and '),
                            em('MECCHA CHAMELEON'),
                            text('.'),
                        ]),
                        p([
                            text('Other streamers scored highly by playing a lot of a single title, such as '),
                            link('https://twitch.tv/call_me_bread', 'call_me_bread'),
                            text(' with 51 streams of '),
                            em('Lethal Company'),
                            text(' and '),
                            link('https://twitch.tv/kept_varrel', 'kept_VARREL'),
                            text(' with 23 of '),
                            em('Super Smash Bros. Ultimate'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/ponce', 'Ponce')],
                                    '99',
                                    '14',
                                    '3,451',
                                    '5',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                    '99',
                                    '19',
                                    '1,436',
                                    '8',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                    '99',
                                    '19',
                                    '1,042',
                                    '6',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '98',
                                    '11',
                                    '10,656',
                                    '6',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/paache_', 'paache_')],
                                    '98',
                                    '11',
                                    '1,761',
                                    '4',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/hcjustin', 'HCJustin')],
                                    '98',
                                    '14',
                                    '527',
                                    '4',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '98',
                                    '20',
                                    '451',
                                    '5',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '98',
                                    '20',
                                    '385',
                                    '4',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                                    ],
                                    '98',
                                    '21',
                                    '352',
                                    '5',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '35',
                                    '258',
                                    '7',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '98',
                                    '27',
                                    '224',
                                    '16',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/call_me_bread', 'call_me_bread')],
                                    '98',
                                    '55',
                                    '206',
                                    '3',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                    '98',
                                    '33',
                                    '175',
                                    '4',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/x33n', 'X33N')],
                                    '98',
                                    '140',
                                    '169',
                                    '6',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '98',
                                    '22',
                                    '161',
                                    '7',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' dominated July. We captured 101k streams of the game, more than the next 16 games combined and seven times more than second place '),
                            em('Among Us'),
                            text('. Activity has been '),
                            link('/party-central?from=2026-07-01T00%3A00%3A00.000Z&to=2026-08-01T00%3A00%3A00.000Z&game=1820839024', 'slowing down'),
                            text(' across the month, but it is still dominating even in the '),
                            link('/party-central?y=game&from=2026-07-25T00%3A00%3A00.000Z&to=2026-08-01T00%3A00%3A00.000Z', 'last days of July'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '101,165', '61,209', '41'],
                                ['2', 'Among Us', '13,877', '7,903', '24'],
                                ['3', 'Fall Guys', '13,089', '6,089', '16'],
                                ['4', 'Super Smash Bros. Ultimate', '11,085', '3,310', '18'],
                                ['5', 'Gartic Phone', '10,251', '2,923', '23'],
                                ['6', 'Words On Stream', '7,293', '600', '15'],
                                ['7', 'Lethal Company', '6,458', '3,228', '12'],
                                ['8', 'Super Battle Golf', '5,732', '4,469', '31'],
                                ['9', 'Mario Kart World', '5,615', '2,250', '31'],
                                ['10', 'Diablo II: Resurrected', '5,116', '895', '17'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Maitre des Fleurs'),
                            text(' led again this month because of '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(". Their three appearances averaged 3,879 viewers and brought the game's average up significantly on a low volume of streams. "),
                            em('Monopoly: Star Wars Heroes vs. Villains'),
                            text(' also stood out, reaching 85 streamers and averaging 269 viewers. Appearances from '),
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(', '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(', and '),
                            link('https://twitch.tv/sykkuno', 'Sykkuno'),
                            text(' supplied 94% of all viewers recorded for the game. '),
                            em('Forgotten Playland'),
                            text(' got a one-time boost because of a collab stream between '),
                            link('https://twitch.tv/limealicious', 'Limealicious'),
                            text(', '),
                            link('https://twitch.tv/ray', 'Ray'),
                            text(', '),
                            link('https://twitch.tv/geega', 'GEEGA'),
                            text(', and '),
                            link('https://twitch.tv/lovelymomo', 'lovelymomo'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Maitre des Fleurs', '630', '19', '10'],
                                ['2', 'Forgotten Playland', '616', '10', '9'],
                                ['3', 'Who Wants to Be a Millionaire', '431', '28', '25'],
                                ['4', 'HAM: The Game', '276', '24', '23'],
                                [
                                    '5',
                                    'Monopoly: Star Wars Heroes vs. Villains',
                                    '269',
                                    '142',
                                    '85',
                                ],
                                ['6', 'Lycans', '213', '155', '79'],
                                ['7', 'Bomberman 64', '189', '16', '9'],
                                ['8', 'Mushroom Simulator Co-op', '177', '14', '14'],
                                ['9', 'Deduce Together', '170', '36', '25'],
                                ['10', 'Trivia Tricks', '165', '14', '13'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            text("July's most widely streamed games were the same as June, although their order changed. "),
                            em('MECCHA CHAMELEON'),
                            text(' is largely responsible for the huge boost in streamers playing party games during the month, with at least 61k streamers joining in. '),
                            em('Super Battle Golf'),
                            text(' made the biggest move, rising from sixth to fourth as its streamer count grew 49%. The rebound lined up with a '),
                            link('https://steamcommunity.com/app/4069520/announcements/', 'July 16 update and free weekend'),
                            text(', when the game added a team mode, a course, and new items.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '61,209', '101,165'],
                                ['2', 'Among Us', '7,903', '13,877'],
                                ['3', 'Fall Guys', '6,089', '13,089'],
                                ['4', 'Super Battle Golf', '4,469', '5,732'],
                                ['5', 'Super Smash Bros. Ultimate', '3,310', '11,085'],
                                ['6', 'Lethal Company', '3,228', '6,458'],
                                ['7', 'Gartic Phone', '2,923', '10,251'],
                                ['8', 'Mario Kart World', '2,250', '5,615'],
                                ['9', 'Jackbox Party Packs', '1,907', '3,936'],
                                ['10', 'Golf With Your Friends', '1,838', '2,581'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Stickiness is the average number of tracked streams per streamer. '),
                            em('nanos world'),
                            text(' set another high at 15.5, up from 14.9 in June and 13.1 in May. Its streamer count fell from 213 in May to 141 in July, so the rising result came from a smaller group playing more frequently. There were no channels dominating streams, so the stickiness is spread widely across the community. On the other hand, '),
                            em('Fruit Mountain'),
                            text(' made the list because of repeat play from a single player: '),
                            link('https://twitch.tv/dailyelectricianyt', 'DailyElectricIanYT'),
                            text(' accounted for 71% of the streams for the puzzle game.'),
                        ]),
                        p([
                            em('Pinball'),
                            text(' remained unusually sticky but fell from 10.5 in June to 7.7 in July. Its total streamer count barely changed, but the top three channels in June all dropped their stream volume significantly in the most recent month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '15.5', '2,183', '141'],
                                ['2', 'Ragnarok X: Next Generation', '12.7', '305', '24'],
                                ['3', 'Words On Stream', '12.2', '7,293', '600'],
                                ['4', 'Musical Vibes', '11.8', '272', '23'],
                                [
                                    '5',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '7.8',
                                    '235',
                                    '30',
                                ],
                                ['6', 'Pinball', '7.7', '1,250', '162'],
                                ['7', 'Gartic on Stream', '6.8', '250', '37'],
                                [
                                    '8',
                                    'The Legend of Heroes: Trails of Cold Steel II',
                                    '6.7',
                                    '207',
                                    '31',
                                ],
                                ['9', 'Placid Plastic Duck Simulator', '6.5', '1,653', '256'],
                                ['10', 'Fruit Mountain', '6.4', '219', '34'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://www.nintendo.com/us/store/products/culdcept-begins-switch/', [em('Culdcept Begins')]),
                            text(' has players roll dice around a board, use creature cards to claim territory, and battle for rival spaces. Most of its July activity came from Japanese-language channels.'),
                        ]),
                        p([
                            link('https://store.steampowered.com/app/4112070/an_Eggstremely_Hard_Game/', [em('An Eggstremely Hard Game')]),
                            text(' is a cooperative physics game about two geese carrying a fragile egg through obstacles. It reached more streamers than any other July release, recording 377 streams from 322 streamers in its first eight days. '),
                            link('https://store.steampowered.com/app/2802740/Heave_Ho_2/', [
                                em('Heave Ho 2'),
                            ]),
                            text(', another two-to-four-player physics game, recorded 130 streams from 98 streamers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Culdcept Begins', 'Jul 16', '559', '137', '15'],
                                ['An Eggstremely Hard Game', 'Jul 24', '377', '322', '107'],
                                ['Heave Ho 2', 'Jul 16', '130', '98', '160'],
                                ['Data Breach', 'Jul 21', '55', '27', '6'],
                                ['Drunk Dad Vs Family', 'Jul 7', '17', '17', '21'],
                                ['Acting Out', 'Jul 1', '14', '14', '7'],
                                ['Project Corner', 'Jul 23', '6', '3', '2'],
                                ['Nerds Revenge', 'Jul 7', '4', '4', '6'],
                                ['Beetleball', 'Jul 30', '2', '2', '2'],
                                ['Froggle', 'Jul 31', '1', '1', '5'],
                                ['Turnip Rush', 'Jul 1', '1', '1', '4'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('Our tracker did not record Twitch activity for 36 other party games released in July. They are listed below by release date.'),
                        list({
                            items: [
                                'Problematic (Jul 1)',
                                'Strafest (Jul 1)',
                                'Relax & Learn World Heritage (Jul 2)',
                                'Slot & Learn Hangul (Jul 2)',
                                'Tanky Tanks: Reloaded (Jul 2)',
                                'Toilet: Urinal Game (Jul 6)',
                                'Brainrot Card Market Simulator (Jul 7)',
                                'Buzzword (Jul 8)',
                                'Card Party Showdown (Jul 9)',
                                'Make it! Shaved Ice (Jul 9)',
                                'Memorize the Evidence: 1-Minute Memory Mystery (Jul 9)',
                                'Whys & Wonders Secrets of the Universe (Jul 9)',
                                'Dig for TCG Cards With Your Friends (Jul 10)',
                                'Forest of Deceit (Jul 13)',
                                'Save the President: Trust Issues (Jul 13)',
                                'Difficult (Jul 14)',
                                'All But Weak (Jul 15)',
                                'Instant Sports 2 (Jul 16)',
                                'Whys & Wonders Secrets of Our Bodies (Jul 16)',
                                'Leap of Faith (Jul 17)',
                                'The GIF Game (Jul 17)',
                                'Tinny Strays (Jul 17)',
                                '100 Korean Hidden Cats (Jul 18)',
                                'Rails & Rivals (Jul 20)',
                                'Watch Yo Plane (Jul 21)',
                                'Scales of Silence (Jul 22)',
                                'Super Goldfish Scooping (Jul 23)',
                                'Driving School Sim (Jul 25)',
                                'Backfire Brigade (Jul 27)',
                                'Meow Moments: Celebrating Frost & Flora (Jul 29)',
                                'Fortnite Festival: Season 15 (Jul 30)',
                                'Sponge Break (Jul 30)',
                                'The Person Living Next Door (Jul 30)',
                                'Batman: Caped Crusader - Chronicles (Jul 31)',
                                'Knockout Battle (Jul 31)',
                                'Ocean Shark Survival: Float Home Builder Simulator (Jul 31)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/x33n', 'X33N'),
                            text(' was the most notable creator on this list. We found the channel live with a party game on 30 of 31 days, up from six days in June, across 29 separate Twitch broadcasts. Of its 140 observations, 122 came from '),
                            em('Words On Stream'),
                            text('. The remaining 18 covered five other games, and the channel averaged 169 viewers.'),
                        ]),
                        p([
                            text('The seven channels above X33N each focused on one game and were found live between 143 and 208 times. That makes X33N unusual here: it combined a near-daily schedule with several games and a larger audience than most of the high-activity channels.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '208',
                                    '146',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '207',
                                    '64',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '207',
                                    '10',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '206',
                                    '30',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '204',
                                    '20',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '198',
                                    '20',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                    '143',
                                    '12',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/x33n', 'X33N')],
                                    '140',
                                    '169',
                                    '6',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/africaalbion', 'AfricaAlbion')],
                                    '118',
                                    '86',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '117',
                                    '65',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 observations',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' led with an average of 39,635 viewers across five observations of '),
                            em('Among Us'),
                            text(' and '),
                            em('MECCHA CHAMELEON'),
                            text('. Eight of the top 10 played '),
                            em('MECCHA CHAMELEON'),
                            text(', including '),
                            link('https://twitch.tv/stableronaldo', 'stableronaldo'),
                            text(' and '),
                            link('https://twitch.tv/zarbex', 'zarbex'),
                            text(', who were two of the five new streamers to the list this month. The two exceptions were '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', who appeared with four games including '),
                            em('Among Us'),
                            text(' and '),
                            em('Super Battle Golf'),
                            text(', and '),
                            link('https://twitch.tv/fps_shaka', 'fps_shaka'),
                            text(', who played '),
                            em('Goose Goose Duck'),
                            text(' and '),
                            em('Dokapon Kingdom: Connect'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Observations',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '39,635',
                                    '5',
                                    '2',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                    '36,008',
                                    '2',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                    '33,776',
                                    '4',
                                    '4',
                                ],
                                [
                                    '4',
                                    [
                                        link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                    ],
                                    '33,133',
                                    '3',
                                    '2',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/stableronaldo', 'stableronaldo')],
                                    '28,783',
                                    '2',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/zarbex', 'zarbex')],
                                    '24,674',
                                    '3',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/xqc', 'xQc')],
                                    '24,177',
                                    '3',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/squeezie', 'Squeezie')],
                                    '23,035',
                                    '2',
                                    '2',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                    '21,979',
                                    '3',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/rubius', 'Rubius')],
                                    '20,800',
                                    '3',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(' played the widest selection with 16 party games, up from 10 in June. The channel appeared 27 times, yet still averaged 224 viewers.'),
                        ]),
                        p([
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(' stood out for combining variety with a much larger audience. KYR doubled from four games in June to eight in July while averaging 1,436 viewers. Half of the July observations came from '),
                            em('MECCHA CHAMELEON'),
                            text(', while the other half spread across seven games.'),
                        ]),
                        p([
                            link('https://twitch.tv/cheesy', 'Cheesy'),
                            text(' joined in the '),
                            em('MECCHA CHAMELEON'),
                            text(' fun while also incorporating other party games such as '),
                            em('Jackbox Party Packs'),
                            text(' and '),
                            em('Gartic Phone'),
                            text(' into their streaming schedule.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Observations',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '16',
                                    '27',
                                    '224',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '14',
                                    '49',
                                    '18',
                                ],
                                [
                                    '3',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '11',
                                    '79',
                                    '74',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/cheesy', 'Cheesy')],
                                    '9',
                                    '20',
                                    '132',
                                ],
                                [
                                    '5',
                                    [
                                        link('https://twitch.tv/2slow2serioustv', '2slow2seriousTV'),
                                    ],
                                    '9',
                                    '17',
                                    '37',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/pjghangouts', 'PJGHangouts')],
                                    '9',
                                    '12',
                                    '10',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                    '8',
                                    '19',
                                    '1,436',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/xmarksthespot26', 'XmarkstheSpot26'),
                                    ],
                                    '8',
                                    '13',
                                    '74',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '7',
                                    '35',
                                    '258',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '7',
                                    '22',
                                    '161',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('German streams nearly doubled, and Chinese streams grew 91%. Russian and Japanese also grew quickly this month. Compare '),
                            link('/party-central?display=data&x=language&y=time&grain=month&from=2026-05-01T00%3A00%3A00.000Z&to=2026-08-01T00%3A00%3A00.000Z', 'languages month over month'),
                            text(' in Party Central.'),
                        ]),
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' made up more than half of all German, Chinese, and Polish streams in July. The game had its largest share of party-game activity in those three language groups.'),
                        ]),
                        p([
                            text('Portuguese streams more than doubled from June, in large part thanks to the popularity of '),
                            em('Ragnarok Origin: Classic'),
                            text('. Together with '),
                            em('MECCHA CHAMELEON'),
                            text(", it accounted for 92% of the language's growth."),
                        ]),
                        p([
                            text('Italian had the largest average audience at 106 viewers, but that result came mostly from '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text('. Excluding Tumblurr, Italian would have averaged about 35 viewers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '159,457', '68,065', '23'],
                                ['2', 'Spanish', '23,015', '11,042', '21'],
                                ['3', 'German', '17,410', '7,701', '63'],
                                ['4', 'Russian', '14,679', '6,467', '27'],
                                ['5', 'French', '14,668', '5,828', '35'],
                                ['6', 'Japanese', '13,463', '6,249', '28'],
                                ['7', 'Portuguese', '9,539', '4,365', '36'],
                                ['8', 'Chinese', '5,831', '2,609', '34'],
                                ['9', 'Italian', '1,897', '919', '106'],
                                ['10', 'Polish', '1,652', '877', '78'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '54,642', '33,164'],
                                        ['2', 'Fall Guys', '7,824', '3,756'],
                                        ['3', 'Among Us', '7,704', '4,310'],
                                        ['4', 'Super Smash Bros. Ultimate', '7,394', '2,110'],
                                        ['5', 'Words On Stream', '6,366', '511'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Forgotten Playland', '770', '8'],
                                        ['2', 'Who Wants to Be a Millionaire', '571', '21'],
                                        [
                                            '3',
                                            'Monopoly: Star Wars Heroes vs. Villains',
                                            '359',
                                            '106',
                                        ],
                                        ['4', 'Bomberman 64', '273', '11'],
                                        ['5', 'Paddle Paddle Paddle', '249', '44'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '207',
                                            '64',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '207',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '206',
                                            '30',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mrpeanut1028', 'MrPeanut1028'),
                                            ],
                                            '205',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '204',
                                            '20',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                            '36,008',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                            ],
                                            '33,133',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/stableronaldo', 'stableronaldo'),
                                            ],
                                            '28,783',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/xqc', 'xQc')],
                                            '24,177',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lacy', 'Lacy')],
                                            '16,243',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '7,283', '4,924'],
                                        ['2', 'Among Us', '2,085', '1,218'],
                                        ['3', 'Gartic Phone', '1,865', '549'],
                                        ['4', 'Super Smash Bros. Ultimate', '1,679', '550'],
                                        ['5', 'Fall Guys', '1,650', '886'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Suika Game', '825', '22'],
                                        ['2', 'Just Dance', '423', '16'],
                                        ['3', 'Wii Sports', '296', '29'],
                                        ['4', 'Keep Talking and Nobody Explodes', '218', '38'],
                                        ['5', 'Pizza House Simulator', '73', '109'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '64',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xgeera', 'xGeera')],
                                            '43',
                                            '60',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/elkurama_', 'elkurama_')],
                                            '42',
                                            '25',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/venconbob', 'VenconBOB')],
                                            '38',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/0xc4r', '0xc4r')],
                                            '37',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/rubius', 'Rubius')],
                                            '20,800',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/peereira7', 'Peereira7')],
                                            '9,370',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/aldo_geo', 'aldo_geo')],
                                            '5,714',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/knekro', 'knekro')],
                                            '5,136',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vegetta777', 'VEGETTA777')],
                                            '4,177',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '9,921', '5,105'],
                                        ['2', 'Among Us', '1,326', '706'],
                                        ['3', 'Fall Guys', '756', '353'],
                                        ['4', 'Golf With Your Friends', '587', '349'],
                                        ['5', 'Mario Kart 8 Deluxe', '430', '218'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'An Eggstremely Hard Game', '221', '30'],
                                        ['2', 'Among Us', '133', '1,326'],
                                        ['3', "Liar's Bar", '93', '66'],
                                        ['4', 'MECCHA CHAMELEON', '80', '9,921'],
                                        ['5', 'Monopoly', '77', '30'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '79',
                                            '74',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                            '58',
                                            '24',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sebbel3105', 'Sebbel3105')],
                                            '47',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/drache_proky', 'Drache_Proky'),
                                            ],
                                            '43',
                                            '30',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/grumbeerknoll', 'Grumbeerknoll'),
                                            ],
                                            '42',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '39,635',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/zarbex', 'zarbex')],
                                            '24,674',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/montanablack88', 'MontanaBlack88'),
                                            ],
                                            '16,426',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/handofblood', 'HandOfBlood')],
                                            '10,472',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xrohat', 'xRohat')],
                                            '8,018',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '6,305', '3,873'],
                                        ['2', 'Gartic Phone', '2,875', '719'],
                                        ['3', 'Jackbox Party Packs', '765', '264'],
                                        ['4', 'Diablo II: Resurrected', '503', '67'],
                                        ['5', 'Lethal Company', '463', '208'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'HAM: The Game', '905', '7'],
                                        ['2', 'Casino', '521', '17'],
                                        ['3', 'An Eggstremely Hard Game', '252', '26'],
                                        ['4', 'Mushroom Simulator Co-op', '205', '12'],
                                        ['5', 'Codenames', '184', '7'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '208',
                                            '146',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/molya_requiem', 'molya_requiem'),
                                            ],
                                            '63',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kusobaki', 'Kusobaki')],
                                            '61',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/irrrrrrrris', 'irrrrrrrris')],
                                            '55',
                                            '29',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/risuchka', 'Risuchka')],
                                            '48',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/morphe_ya', 'morphe_ya')],
                                            '6,052',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mashtakof', 'MashtakOF')],
                                            '5,955',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '4,748',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '3,833',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/c_a_k_e', 'C_a_k_e')],
                                            '3,320',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '4,996', '2,985'],
                                        ['2', 'nanos world', '2,132', '136'],
                                        ['3', 'Fall Guys', '1,102', '416'],
                                        ['4', 'Among Us', '1,036', '571'],
                                        ['5', 'Super Smash Bros. Ultimate', '694', '241'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Mario Party Jamboree', '824', '71'],
                                        ['2', 'Maitre des Fleurs', '630', '19'],
                                        ['3', 'Lycans', '223', '146'],
                                        ['4', 'Wii Party', '177', '19'],
                                        ['5', 'Codenames', '101', '55'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '90',
                                            '82',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '70',
                                            '72',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/heaven_et_alex', 'heaven_et_alex'),
                                            ],
                                            '68',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '64',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/jiizk', 'JIIZK')],
                                            '63',
                                            '26',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/squeezie', 'Squeezie')],
                                            '23,035',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/zerator', 'ZeratoR')],
                                            '10,477',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gotaga', 'Gotaga')],
                                            '7,264',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/locklear', 'Locklear')],
                                            '5,214',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/maximebiaggi', 'MaximeBiaggi'),
                                            ],
                                            '3,962',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '5,062', '3,515'],
                                        ['2', 'Mahjong Soul', '1,838', '671'],
                                        ['3', 'Super Smash Bros. Ultimate', '727', '200'],
                                        ['4', 'Beat Saber', '693', '159'],
                                        ['5', 'Culdcept Begins', '461', '106'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Dokapon Kingdom: Connect', '1,052', '24'],
                                        ['2', 'Goose Goose Duck', '604', '93'],
                                        ['3', 'First Class Trouble', '233', '21'],
                                        ['4', 'Deduce Together', '170', '36'],
                                        [
                                            '5',
                                            'Clubhouse Games: 51 Worldwide Classics',
                                            '88',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sabomkw', 'さっぼさん')],
                                            '38',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/makosu', 'makosu')],
                                            '36',
                                            '27',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '35',
                                            '258',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/takum1rmu', 'おかでぃーです')],
                                            '34',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '33',
                                            '175',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                            '21,979',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gutitubo', 'ぐちつぼ')],
                                            '4,308',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/jasper7se', 'Jasper7se')],
                                            '4,046',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/yutapongo', 'yutapongo')],
                                            '3,004',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hanjoudesu', 'はんじょう')],
                                            '2,993',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '4,024', '2,309'],
                                        ['2', 'Ragnarok Origin: Classic', '869', '178'],
                                        ['3', 'Gartic Phone', '521', '148'],
                                        ['4', 'Words On Stream', '411', '57'],
                                        ['5', 'Fall Guys', '383', '151'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'An Eggstremely Hard Game', '602', '24'],
                                        ['2', 'Trail & Error', '272', '13'],
                                        ['3', 'Fruit Mountain', '120', '13'],
                                        ['4', 'Whatsamusic', '106', '15'],
                                        ['5', 'Ultimate Chicken Horse', '83', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                                            ],
                                            '118',
                                            '86',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '117',
                                            '65',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vanilian', 'Vanilian')],
                                            '66',
                                            '100',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dyushi', 'DYushi')],
                                            '56',
                                            '66',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/miomio', 'miomio')],
                                            '52',
                                            '49',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/alanzoka', 'alanzoka')],
                                            '18,436',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/cellbit', 'Cellbit')],
                                            '11,239',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/boltz', 'boltz')],
                                            '1,889',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '1,562',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tealzlol', 'Tealzlol')],
                                            '1,120',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '3,262', '1,875'],
                                        ['2', 'Mahjong Soul', '523', '238'],
                                        ['3', 'Mabinogi', '400', '52'],
                                        ['4', 'Party Animals', '182', '94'],
                                        ['5', 'Amatsuki Mahjong', '152', '83'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Heave Ho 2', '659', '18'],
                                        ['2', 'Ragnarok Origin: Classic', '172', '5'],
                                        ['3', 'KLETKA', '86', '6'],
                                        ['4', 'Mahjong Soul', '84', '523'],
                                        ['5', 'Mario Kart World', '74', '27'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/isyourlulu', '雪羽婭露露')],
                                            '29',
                                            '52',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/aldia1204', '艾爾狄亞')],
                                            '29',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/daaafuuu', '草莓紅豆大福')],
                                            '27',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/yuwuamane', '雨霧あまね')],
                                            '22',
                                            '19',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/maxdeng', '新八姬')],
                                            '21',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/kr1stw', '西南69')],
                                            '4,640',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kspksp', 'KSPKSP')],
                                            '2,884',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,357',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/yuzumi_neon', '橙ゆずみ_霓')],
                                            '2,286',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hennie2001', '烟花蹦蹦蹦')],
                                            '1,546',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '542', '334'],
                                        ['2', 'Fall Guys', '264', '125'],
                                        ['3', 'Among Us', '214', '124'],
                                        ['4', 'Gartic Phone', '142', '57'],
                                        ['5', 'Super Smash Bros. Ultimate', '81', '31'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', "Liar's Bar", '4,765', '7'],
                                        ['2', 'Super Battle Golf', '1,485', '27'],
                                        ['3', 'Among Us', '225', '214'],
                                        ['4', 'Fall Guys', '166', '264'],
                                        ['5', 'Just Dance 2026 Edition', '57', '11'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '21',
                                            '26',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/aleexxxxx___', 'aleexxxxx___'),
                                            ],
                                            '20',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/queen_giorgia', 'Queen_Giorgia'),
                                            ],
                                            '17',
                                            '74',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nofacexe', 'Nofacexe')],
                                            '17',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/blooodyangel', 'blooodyangel'),
                                            ],
                                            '15',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                            '33,776',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '3,250',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/manuuxo', 'ManuuXO')],
                                            '3,140',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/leutum', 'LEUTUM')],
                                            '1,744',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vivalafazza', 'VivaLaFazza')],
                                            '882',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__polish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'MECCHA CHAMELEON', '845', '541'],
                                        ['2', 'Fall Guys', '133', '38'],
                                        ['3', 'Gartic Phone', '109', '42'],
                                        ['4', 'Among Us', '102', '66'],
                                        ['5', 'Diablo II: Resurrected', '87', '24'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Gartic Phone', '455', '109'],
                                        ['2', 'Whatsamusic', '165', '12'],
                                        ['3', 'Brain Show', '107', '12'],
                                        ['4', 'MECCHA CHAMELEON', '78', '845'],
                                        ['5', 'Diablo II: Resurrected', '38', '87'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '20',
                                            '27',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/krzysiolek_tv', 'krzysiolek_tv'),
                                            ],
                                            '18',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '12',
                                            '178',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/eybiak', 'Eybiak')],
                                            '11',
                                            '53',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/monofotoalaajmbot', 'monofotoalaajmbot'),
                                            ],
                                            '11',
                                            '33',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ewroon', 'EWROON')],
                                            '11,539',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '3,023',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/graf', 'Graf')],
                                            '2,501',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/slayproxx', 'Slayproxx')],
                                            '2,026',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/overpow', 'Overpow')],
                                            '1,448',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            sections: [
                section({
                    id: 'game-deep-dives__ragnarok-origin-classic',
                    title: 'Ragnarok Origin: Classic',
                    collapsible: true,
                    blocks: [
                        p('1,357 streams | 290 streamers | 4.7 stickiness | 53 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__ragnarok-origin-classic__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dyushi', 'DYushi')],
                                            '54',
                                            '67',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ttvkaiserr', 'ttvkaiserr')],
                                            '39',
                                            '31',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/zeinweb3', 'zeinweb3')],
                                            '30',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ouluh', 'OuluH')],
                                            '22',
                                            '668',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/littlefattygg', 'LittleFattyGG'),
                                            ],
                                            '22',
                                            '66',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__ragnarok-origin-classic__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tealzlol', 'Tealzlol')],
                                            '1,120',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ouluh', 'OuluH')],
                                            '668',
                                            '22',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/zeusghostz', 'ZeusGhostz')],
                                            '231',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/minharte', 'MinhArte')],
                                            '230',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/cahos_gaming', 'CAHOS_GAMING'),
                                            ],
                                            '200',
                                            '15',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('2,183 streams | 141 streamers | 15.5 stickiness | 49 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '90',
                                            '82',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '70',
                                            '72',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/heaven_et_alex', 'heaven_et_alex'),
                                            ],
                                            '68',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '64',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ospride', 'OsPride')],
                                            '62',
                                            '52',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '709',
                                            '29',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '577',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mowraa', 'Mowraa')],
                                            '381',
                                            '22',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/betr0uni', 'Betr0uni')],
                                            '337',
                                            '27',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aloonea', 'Aloonea')],
                                            '237',
                                            '29',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__gartic-phone',
                    title: 'Gartic Phone',
                    collapsible: true,
                    blocks: [
                        p('10,251 streams | 2,923 streamers | 3.5 stickiness | 23 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__gartic-phone__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sen_927', 'sen_927')],
                                            '84',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vanilian', 'Vanilian')],
                                            '59',
                                            '104',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/irrrrrrrris', 'irrrrrrrris')],
                                            '55',
                                            '29',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '54',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kusobaki', 'Kusobaki')],
                                            '47',
                                            '18',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__gartic-phone__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ewroon', 'EWROON')],
                                            '11,527',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tinakitten', 'TinaKitten')],
                                            '3,360',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/foolish', 'Foolish')],
                                            '2,854',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ashswag', 'ashswag')],
                                            '2,405',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tubbo', 'Tubbo')],
                                            '2,069',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('5,116 streams | 895 streamers | 5.7 stickiness | 17 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '207',
                                            '64',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/d2kariya', 'd2kariya')],
                                            '59',
                                            '20',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/devildimlight_ttv', 'DevilDimLight_TTV'),
                                            ],
                                            '58',
                                            '25',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '57',
                                            '19',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nikolauses', 'nikolauses')],
                                            '47',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '511',
                                            '25',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kano', 'Kano')],
                                            '407',
                                            '40',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '178',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kypced', 'kypced')],
                                            '126',
                                            '34',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/b4jeczk4', 'B4jeczk4')],
                                            '124',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__riichi-city',
                    title: 'Riichi City',
                    collapsible: true,
                    blocks: [
                        p('620 streams | 192 streamers | 3.2 stickiness | 56 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__riichi-city__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/overlordwhale', 'overlordwhale'),
                                            ],
                                            '25',
                                            '36',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/demonsmaster', 'DemonsMaster'),
                                            ],
                                            '16',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/nautiluslink', 'NautilusLink'),
                                            ],
                                            '10',
                                            '65',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/yukigamipandora', 'YukigamiPandora'),
                                            ],
                                            '9',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kyuumj', 'KyuuMJ')],
                                            '9',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__riichi-city__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '7,907',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lovelymomo', 'lovelymomo')],
                                            '411',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/yu_song', 'yu_song')],
                                            '187',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/stanz', 'Stanz')],
                                            '148',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/qathrin', 'Qathrin')],
                                            '132',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('1,250 streams | 162 streamers | 7.7 stickiness | 23 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/steelvalleypinball', 'SteelValleyPinball'),
                                            ],
                                            '44',
                                            '40',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '34',
                                            '338',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/laser_los', 'laser_los')],
                                            '32',
                                            '36',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/enteryourinitials', 'EnterYourInitials'),
                                            ],
                                            '30',
                                            '28',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '28',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '338',
                                            '34',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                            '258',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/amiga_cammy', 'Amiga_Cammy')],
                                            '69',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/iepinball', 'iepinball')],
                                            '66',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ypsipinball', 'YpsiPinball')],
                                            '55',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('11,085 streams | 3,310 streamers | 3.3 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '198',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '65',
                                            '50',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '36',
                                            '1,212',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/shadowthehedgy', 'shadowthehedgy'),
                                            ],
                                            '33',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/titou_ssb', 'titou_ssb')],
                                            '33',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sakor_', 'Sakor_')],
                                            '1,556',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '1,212',
                                            '36',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '662',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vgbootcamp2', 'VGBootCamp2')],
                                            '532',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mang0', 'mang0')],
                                            '411',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('5,615 streams | 2,250 streamers | 2.5 stickiness | 31 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/thepickyboys', 'thepickyboys'),
                                            ],
                                            '93',
                                            '247',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '56',
                                            '68',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sobrie_', 'Sobrie_')],
                                            '40',
                                            '23',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/aketxz', 'Aketxz')],
                                            '32',
                                            '24',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '24',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/knekro', 'knekro')],
                                            '5,136',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '1,729',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/slrabbit99', '貝克')],
                                            '861',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/seereaxtv', 'SeeReaxTV')],
                                            '774',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/momoti1234', 'りんごもちぃ')],
                                            '577',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,293 streams | 600 streamers | 12.2 stickiness | 15 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '207',
                                            '10',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '206',
                                            '30',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '204',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '188',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                            '143',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/leopard', 'Leopard')],
                                            '530',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/skinnedteen', 'skinnedteen')],
                                            '286',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/boj9', 'Boj9')],
                                            '194',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/catiaflaviaaa', 'catiaflaviaaa'),
                                            ],
                                            '182',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/tocadoscoelhos', 'TocaDosCoelhos'),
                                            ],
                                            '176',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('3,408 streams | 1,276 streamers | 2.7 stickiness | 25 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '50',
                                            '35',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/takum1rmu', 'おかでぃーです')],
                                            '34',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/raisinnnnnn', 'raisinnnnnn')],
                                            '33',
                                            '30',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/gomashiojijii', 'ごましおじじい'),
                                            ],
                                            '28',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/hika_tin115', 'ひかちゃんねる'),
                                            ],
                                            '23',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,357',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mimiluckying', '獅子丸')],
                                            '1,136',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kokujintv', 'kokujintv')],
                                            '1,050',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kettuncyukyuhei', 'けっつん')],
                                            '805',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/niconini11369', '京野妮子')],
                                            '655',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Saturday was the busiest day for the fourth month in a row, and it again had one of the smallest average audiences. The effects from '),
                            em('MECCHA CHAMELEON'),
                            text(' were felt across all days of the week, but one of the biggest increases from last month was on Wednesday, when average streams rose 65%.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,470', '31'],
                                ['Monday', '1,071', '28'],
                                ['Tuesday', '1,074', '24'],
                                ['Wednesday', '1,235', '38'],
                                ['Thursday', '1,177', '30'],
                                ['Friday', '1,376', '28'],
                                ['Saturday', '1,687', '19'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Activity for July peaked around 19:00 UTC, averaging more than 2,000 streams and 50 viewers per stream. There was another noticeable peak of streams between 01:00 and 02:00 UTC, but with lower viewership. The highest-audience hour has changed in each of the last four months as different games become popular and large creators adapt their programming. Explore '),
                            link('/party-central?visualization=heatmap&measure=avgStreamsPerCapture&x=hourUtc&y=dayOfWeekUtc&grain=&from=2026-07-01T00%3A00%3A00.000Z&to=2026-08-01T00%3A00%3A00.000Z', 'July activity by hour and weekday'),
                            text(' in Party Central.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,769', '33'],
                                ['01:00', '2,049', '18'],
                                ['02:00', '2,002', '25'],
                                ['03:00', '1,691', '24'],
                                ['04:00', '1,667', '13'],
                                ['05:00', '1,238', '14'],
                                ['06:00', '1,015', '22'],
                                ['07:00', '682', '14'],
                                ['08:00', '629', '15'],
                                ['09:00', '508', '19'],
                                ['10:00', '533', '22'],
                                ['11:00', '552', '24'],
                                ['12:00', '793', '23'],
                                ['13:00', '994', '32'],
                                ['14:00', '1,076', '29'],
                                ['15:00', '1,222', '34'],
                                ['16:00', '1,173', '33'],
                                ['17:00', '1,185', '32'],
                                ['18:00', '1,604', '32'],
                                ['19:00', '2,038', '50'],
                                ['20:00', '1,791', '41'],
                                ['21:00', '1,726', '36'],
                                ['22:00', '1,520', '24'],
                                ['23:00', '1,625', '33'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-07-top-party-games-and-streamers.js.map